import { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom';
import { CardData, DataShareContact, ShareMethod } from './type';
import { useLazyGetWalletCardPublicQuery, useDownloadWalletCardPublicQuery, useDownloadContactPublicQuery, useDownloadWalletCardPublicGoogleQuery } from '../../../../services/cellcast.v2';
import { skipToken } from '@reduxjs/toolkit/query';
import { PreviewWalletCardContact } from './components/PreviewWalletCard/PreviewWalletCardContact/PreviewWalletCardContact';
import FormWalletCardPublic from './components/FormWalletCardPublic';
import { toast } from 'react-toastify';
import './components/PreviewWalletCard/PreviewWalletCard.scss';
import './WalletCardPublic.scss';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ViewType } from './type';
import { useI18n } from '../../../Utils/_helpers/useI18n';
import SuccessPage from './components/SuccessPage';

export const WalletCardPublic: FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const [cardData, setCardData] = useState<CardData>({} as CardData);
  const [getWalletCardPublic, { data: walletCardData, isLoading }] = useLazyGetWalletCardPublicQuery();
  const [downloadWalletCardId, setDownloadWalletCardId] = useState<string | null>(null);
  const [downloadContactId, setDownloadContactId] = useState<string | null>(null);
  const [dataShareContact, setDataShareContact] = useState<DataShareContact>({} as DataShareContact);
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isAndroid: false,
    isIOS: false,
    screenWidth: 0,
    screenHeight: 0,
    orientation: ''
  });
  const { data: contactBlob, isLoading: isDownloadingContact, error: downloadErrorContact } = useDownloadContactPublicQuery(downloadContactId ? { id: downloadContactId } : skipToken);
  const { data: pkpassBlob, isLoading: isDownloading, error: downloadError } = useDownloadWalletCardPublicQuery(downloadWalletCardId && !deviceInfo.isAndroid ? { id: downloadWalletCardId } : skipToken);
  const { data: googleWalletCard, isLoading: isDownloadingGoogle, error: downloadErrorGoogle } = useDownloadWalletCardPublicGoogleQuery(downloadWalletCardId && deviceInfo.isAndroid ? { id: downloadWalletCardId } : skipToken);
  const [viewType, setViewType] = useState<ViewType>(ViewType.WALLET_CARD);
  const [showPopup, setShowPopup] = useState<boolean>(true); // ToDO
  const [showSuccessPage, setShowSuccessPage] = useState<boolean>(false);
  const { t } = useI18n();
  const handleSuccessClose = useCallback(() => {
    // Try to redirect/open wallet application based on platform
    if (deviceInfo.isIOS) {
      // iOS Wallet deep link
      window.location.href = 'wallet://';
    } else if (deviceInfo.isAndroid) {
      // Open Google Wallet
      window.open('https://pay.google.com', '_blank');
    } else {
      // Fallback: navigate to internal wallet card list page
      window.location.href = '/wallet-card';
    }
  }, [deviceInfo.isIOS, deviceInfo.isAndroid]);

  const detectDevice = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
    const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait';

    setDeviceInfo({
      isMobile: isMobile && !isTablet,
      isTablet,
      isDesktop: !isMobile && !isTablet,
      isAndroid,
      isIOS,
      screenWidth,
      screenHeight,
      orientation
    });
  }, []);

  useEffect(() => {
    document.body.classList.add('wallet-card-public-page');

    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
    document.head.appendChild(viewportMeta);

    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.classList.remove('wallet-card-public-page');
      document.body.style.overflow = '';

      const metaTag = document.querySelector('meta[name="viewport"]');
      if (metaTag) {
        metaTag.remove();
      }
    };
  }, []);

  useEffect(() => {
    detectDevice();

    const handleResize = () => {
      detectDevice();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [detectDevice]);

  useEffect(() => {
    if (cardId) {
      getWalletCardPublic({ id: cardId });
    }
  }, [cardId, getWalletCardPublic]);

  useEffect(() => {
    if (walletCardData?.data) {
      setCardData(walletCardData.data as CardData);

      // Set initial view type based on shareMethod
      if (walletCardData.data.shareMethod) {
        const hasWallet = walletCardData.data.shareMethod.includes(ShareMethod.WALLET);
        const hasContact = walletCardData.data.shareMethod.includes(ShareMethod.CONTACT);

        if (hasWallet && !hasContact) {
          setViewType(ViewType.WALLET_CARD);
        } else if (!hasWallet && hasContact) {
          setViewType(ViewType.CONTACT);
        }
      }
    }
  }, [walletCardData]);

  const handleDownloadWalletCard = useCallback(() => {
    if (cardId) {
      setDownloadWalletCardId(cardId);
    }
  }, [cardId]);

  useEffect(() => {
    if (deviceInfo.isAndroid && googleWalletCard?.data?.googleWalletUrl) {
      const url = googleWalletCard.data.googleWalletUrl;
      window.open(url, '_blank');
      setDownloadWalletCardId(null);
      setShowSuccessPage(true);
    } else if (!deviceInfo.isAndroid && pkpassBlob && pkpassBlob instanceof Blob) {
      const url = window.URL.createObjectURL(pkpassBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pass.pkpass';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      toast.success(t('WALLET_CARD_PUBLIC.DOWNLOAD_SUCCESS'));
      setDownloadWalletCardId(null);
      setShowSuccessPage(true);
    }

    if (downloadError || downloadErrorGoogle) {
      toast.error(t('WALLET_CARD_PUBLIC.DOWNLOAD_ERROR'));
    }
  }, [pkpassBlob, googleWalletCard, deviceInfo.isAndroid]);

  useEffect(() => {
    if (contactBlob && contactBlob instanceof Blob) {
      const url = window.URL.createObjectURL(contactBlob);
      const a = document.createElement('a');
      a.href = url;
      const cleanName = (cardData.fullName || 'contact').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
      a.download = `${cleanName}.vcf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      setDownloadContactId(null);
      setShowSuccessPage(true);
    }
    if (downloadErrorContact) {
      toast.error(t('WALLET_CARD_PUBLIC.DOWNLOAD_ERROR_CONTACT'));
      return;
    }
  }, [contactBlob, downloadErrorContact, cardData.fullName]);

  const handleDownloadContact = useCallback(() => {
    if (cardId) {
      setDownloadContactId(cardId);
    }
  }, [cardId]);


  const containerStyle = useMemo(() =>
    deviceInfo.isMobile || deviceInfo.isTablet
      ? {
        padding: 0,
        display: 'block',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: '#fff'
      }
      : {}
    , [deviceInfo.isMobile, deviceInfo.isTablet]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('COMMON.LOADING')}</span>
        </div>
      </div>
    );
  }

  if (showSuccessPage) {
    return (
      <SuccessPage
        name={cardData.fullName || 'User'}
        dataShareContact={dataShareContact}
        onClose={handleSuccessClose}
      />
    );
  }

  return (
    <div className="wallet-card-public-container position-relative" style={containerStyle}>
      <div className={`w-100 heigth-contact-view`}>
        <PreviewWalletCardContact cardData={cardData} />
      </div>
      <div className="w-100 card-footer shadow">
        <div className="d-flex flex-column justify-content-center align-items-center gap-4" style={{opacity: 0.95}}>
          <div className="row-share-button d-flex flex-column gap-4">
            {cardData.shareMethod?.includes(ShareMethod.WALLET) && (
              <button
                className="btn-save-card fs-4"
                onClick={handleDownloadWalletCard}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('WALLET_CARD_PUBLIC.DOWNLOADING')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-wallet-fill me-2 text-white fs-3"></i>
                    {t('WALLET_CARD_PUBLIC.SAVE_TO_WALLET')}
                  </>
                )}
              </button>
            )}
            {cardData.shareMethod?.includes(ShareMethod.CONTACT) && (
              <button className="btn-save-contact fs-4" onClick={handleDownloadContact}>
                {isDownloadingContact ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('WALLET_CARD_PUBLIC.DOWNLOADING')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus-fill me-2 text-white fs-3"></i>
                    {t('WALLET_CARD_PUBLIC.SAVE_TO_CONTACT')}
                  </>
                )}
              </button>
            )}
          </div>
          <div className="w-100">
            <div className="text-center w-100 fs-4">{t('WALLET_CARD_PUBLIC.POWERED_BY')}</div>
          </div>
        </div>
      </div>
      {/* Popup Modal */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content d-flex flex-column gap-2" onClick={(e) => e.stopPropagation()} style={{backgroundColor: cardData.formBackgroundColor}}>
            <FormWalletCardPublic
              name={cardData.fullName || 'User'}
              userID={cardData.user as string}
              isInPopup={true}
              source={viewType}
              targetID={cardId}
              setShowPopup={setShowPopup}
              picture={cardData.picture || ''}
              setDataShareContact={setDataShareContact}
              promoSettings={cardData.promoSettings || []}
              formSettings={cardData.formSettings || []}
              formBackgroundColor={cardData.formBackgroundColor}
              formLabelColor={cardData.formLabelColor}
              contactIconColor={cardData.contactIconColor}
              vehicleTypeOptions={(cardData.vehicleTypeOptions as any[])?.map((vehicleType) => vehicleType._id) || []}
            />
          </div>
        </div>
      )}
    </div>
  )
}
